import { sva } from '@allenai/varnish-panda-runtime/css';
import type { RecipeVariantProps } from '@allenai/varnish-panda-runtime/types';

/**
 *  Checkboxes can be built with the <input> HTML element, but this can be difficult to style.
 *   Checkbox helps achieve accessible checkboxes that can be styled as needed.
 *
 *  See: https://react-spectrum.adobe.com/react-aria/Checkbox.html
 */

/**
 * @remarks A checkbox recipe
 *
 * @param size - Size of the checkbox, values are `small`, `medium` (default), and `large`
 * @param color - Color of the checkbox, values are:
 *   `primary` (default), `secondary`, `tertiary`, `teal`, and `cream`
 */

const checkboxRecipe = sva({
    slots: ['root', 'box', 'icon'],
    base: {
        root: {
            display: 'flex',
            gap: '[8px]',
            alignItems: 'center',
            cursor: 'pointer',
            color: 'text',
            colorPalette: 'elements.overrides.form.checkbox',
            _disabled: {
                colorPalette: 'elements.overrides.form.checkbox.disabled',
                cursor: 'not-allowed',
            },
            _invalid: {
                colorPalette: 'elements.overrides.form.checkbox.error',
            },
            _componentReadOnly: {
                cursor: 'default',
            },
        },
        box: {
            boxSizing: 'border-box',
            width: '[20px]',
            height: '[20px]',
            flexShrink: '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 'xs',
            border: '[1px solid]',

            // the background, unselected, is just the `default` fill
            backgroundColor: 'elements.overrides.form.checkbox.fill',
            borderColor: 'colorPalette.stroke',

            _groupSelected: {
                borderColor: 'transparent',
                backgroundColor: 'colorPalette.fill',
            },

            _groupHoverNotDisabledNotSelectedNotInvalid: {
                borderColor: 'colorPalette.hovered.stroke',
            },
        },
        icon: {
            width: '[0.625rem]',
            height: '[auto]',
            // this sets the color of the svg
            // if we allow passing in Icons this should probably move to `box`
            color: 'colorPalette.contrast',
        },
    },
    variants: {
        size: {
            small: {
                root: {
                    fontSize: '[12px]',
                },
                box: {
                    width: '[0.75rem]',
                    height: '[0.75rem]',
                },
            },
            medium: {
                root: {
                    fontSize: 'sm',
                },
                box: {
                    width: '[0.875rem]',
                    height: '[0.875rem]',
                },
            },
            large: {
                root: {
                    fontSize: 'md',
                },
                box: {
                    width: '[1.25rem]',
                    height: '[1.25rem]',
                },
                icon: {
                    width: '[0.75rem]',
                },
            },
        },
        color: {
            default: {
                root: {
                    // overrides only apply for selected state
                    // the default root colorPalette is `elements.overrides.form.checkbox`
                    // which provides the unchecked states for `default` (i.e. default fill, stroke, hover)
                    _selected: {
                        colorPalette: 'elements.overrides.form.checkbox.checked',
                    },
                },
            },
            primary: {
                root: {
                    colorPalette: 'elements.primary',
                },
            },
            secondary: {
                root: {
                    colorPalette: 'elements.secondary',
                },
            },
            tertiary: {
                root: {
                    colorPalette: 'elements.tertiary',
                },
            },
        },
    },
    defaultVariants: {
        size: 'medium',
        color: 'primary',
    },
});

type CheckboxRecipeProps = RecipeVariantProps<typeof checkboxRecipe>;

export { checkboxRecipe, checkboxRecipe as default };
export type { CheckboxRecipeProps };
